// ─────────────────────────────────────────────────────────────
// Total Cost of Ownership voor Portugese woningen.
// Gebaseerd op publieke Portugese belasting-tarieven (2024-2025)
// en gemiddelde bouw-/renovatieprijzen per regio.
//
// Tiago Máximo's advies: "de koopprijs is vaak het kleinste deel."
// Deze module maakt dat concreet zichtbaar per listing of regio.
// ─────────────────────────────────────────────────────────────

// Bouwprijzen per regio in EUR/m² — voor renovatie/herbouw incl.
// installaties + afwerking. Bronnen: gangbare PT-architecten +
// Maximo Properties feedback. Drie niveaus: minimum / typisch /
// premium-afwerking.
export const CONSTRUCTION_COSTS = {
  "Alentejo Coast":          { min: 1100, typical: 1400, premium: 1900 },
  "Setúbal / Arrábida":      { min: 1300, typical: 1600, premium: 2100 },
  "Silver Coast":            { min: 900,  typical: 1200, premium: 1600 },
  "Western Algarve":         { min: 1200, typical: 1500, premium: 2000 },
  "Eastern Algarve":         { min: 1100, typical: 1400, premium: 1800 },
  "Aveiro Coast":            { min: 900,  typical: 1100, premium: 1500 },
  "Costa Verde":             { min: 850,  typical: 1100, premium: 1500 },
  _default:                  { min: 1000, typical: 1300, premium: 1700 },
};

// IMT — Imposto Municipal sobre as Transmissões.
// Vereenvoudigde brackets voor 2024-2025 voor woonhuizen, secundair
// verblijf op het vasteland. Numeriek gemiddelde per bracket.
//   Verifieer altijd met je solicitor — tarieven veranderen jaarlijks.
const IMT_BRACKETS = [
  { max: 101917,  rate: 0,    deduct: 0 },
  { max: 139412,  rate: 0.02, deduct: 2038 },
  { max: 190086,  rate: 0.05, deduct: 6221 },
  { max: 316772,  rate: 0.07, deduct: 10023 },
  { max: 633453,  rate: 0.08, deduct: 13191 },
  { max: 1102920, rate: 0.06, deduct: 0,    flat: true },
  { max: Infinity, rate: 0.075, deduct: 0,  flat: true },
];

export function calculateIMT(price) {
  if (!price || price <= 101917) return 0;
  for (const b of IMT_BRACKETS) {
    if (price <= b.max) {
      return Math.max(0, Math.round(b.flat ? price * b.rate : price * b.rate - b.deduct));
    }
  }
  return 0;
}

// Stamp duty (Imposto do Selo): 0.8% op koopprijs
export function calculateStamp(price) {
  return Math.round((price || 0) * 0.008);
}

// Hoofdberekening — geeft alle subkosten voor budgettering.
//
// opts:
//   renovationLevel: "min" | "typical" | "premium"   default typical
//   builtArea:       m² renoveerbaar               default = listing.area_built_m2 || 150
//   needsNewRoad:    boolean — significant grondwerk default false
//   region:          override (anders concelho.region)
export function calculateTCO(listing, concelho, opts = {}) {
  const {
    renovationLevel = "typical",
    builtArea = listing?.area_built_m2 || 150,
    needsNewRoad = false,
  } = opts;
  const region = opts.region || concelho?.region || "_default";
  const price = listing?.price_eur || 0;
  const costs = CONSTRUCTION_COSTS[region] || CONSTRUCTION_COSTS._default;
  const ppsm = costs[renovationLevel] || costs.typical;

  // ── Aankoop-belastingen + administratie ──
  const imt = calculateIMT(price);
  const stamp = calculateStamp(price);
  const registration = 500;          // Conservatieve schatting
  const solicitorFees = 2500;        // Advogado / solicitador
  const acquisition = imt + stamp + registration + solicitorFees;

  // ── Renovatie / bouw ──
  const renovation = Math.round(builtArea * ppsm);

  // ── Vakman-kosten ──
  const architect = Math.round(renovation * 0.10);  // ~10% van bouwkost
  const permits   = Math.round(renovation * 0.03 + 3000);

  // ── Infrastructuur (rural property typisch) ──
  const infrastructure = {
    water:     5000,    // Boorput + filtratie + pomp
    electric:  3000,    // Aansluiting + meter (variabel)
    septic:    7000,    // Septic tank + drainage (verplicht buiten dorpskern)
    road:      needsNewRoad ? 14000 : 2500,
  };
  const infraTotal = infrastructure.water + infrastructure.electric +
                     infrastructure.septic + infrastructure.road;

  // ── Contingency ──
  // 10% reserve op renovatie + infra (Tiago: "er zijn altijd
  // onverwachte kosten"). Architect + permits zijn voorspelbaarder
  // dus die rekenen we exact.
  const contingency = Math.round((renovation + infraTotal) * 0.10);

  const total = price + acquisition + renovation + architect + permits +
                infraTotal + contingency;

  const onTopPct = price > 0 ? Math.round(((total - price) / price) * 100) : 0;

  // ── Jaarlijkse kosten ──
  // Cadastrale waarde (VPT) is meestal 50-70% van marktprijs
  const cadastral = Math.round(price * 0.6);
  // IMI-tarief uit concelho-data (default 0.4%)
  const imiRate = concelho?.imi_rate_2025 || 0.4;
  const yearly = {
    imi:         Math.round(cadastral * (imiRate / 100)),
    insurance:   Math.round(price * 0.002),
    maintenance: Math.round((price + renovation) * 0.01),
    utilities:   4000,
  };
  yearly.total = yearly.imi + yearly.insurance + yearly.maintenance + yearly.utilities;

  return {
    price,
    acquisition,
    acquisitionDetail: { imt, stamp, registration, solicitorFees },
    renovation,
    renovationDetail:  { ppsm, builtArea, level: renovationLevel, region },
    architect,
    permits,
    infrastructure: { ...infrastructure, total: infraTotal },
    contingency,
    total,
    onTopPct,
    cadastral,
    yearly,
  };
}

// Indicatieve TCO per concelho — voor de TownModal "wat kost wonen hier".
// Gebaseerd op gemiddelde grond/huisprijs in de regio (cureren in concelho_facts).
export function calculateIndicativeTCO(concelho) {
  const region = concelho?.region || "_default";
  // Gebruik een typische €600k woning + 150m² renovatie als ankerpunt
  const fakeListing = { price_eur: 600000, area_built_m2: 150 };
  return calculateTCO(fakeListing, concelho, { renovationLevel: "typical" });
}

export function formatEurCompact(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return "€" + (n / 1_000_000).toFixed(2).replace(".", ",") + "M";
  if (n >= 1000)      return "€" + Math.round(n / 1000) + "k";
  return "€" + n;
}
